/**
 * Single canonical upload pipeline for the entire app.
 *
 * Flow (any file, any size, any format):
 *   1. Detect format. If browser-incompatible (HEIC/HEIF), transcode to JPEG
 *      q95 in the browser via heic2any (libheif wasm). Same fidelity, plays
 *      everywhere.
 *   2. POST /upload/sign        → server validates quota + returns presigned PUT
 *   3. PUT directly to Wasabi   → no API server bandwidth
 *   4. POST /upload/finalize    → server tracks bytes against tenant.storageUsed
 *
 * For panoramas, callers usually want a thumbnail too. uploadPanorama() handles
 * that automatically (canvas-based downscale, then second presign upload).
 */

import { useUploadStore } from '@/store/uploadStore';
import { getApiBase } from '@/lib/api-base';
import { SAFE_VIDEO_WIDTH } from '@/lib/video-support';

// Resolvido, não escrito à mão: a API mora em api.my360.site em produção e
// em /api no desenvolvimento. Um '/api' fixo aqui mandaria o upload para o
// host errado assim que o front rodasse em app.my360.site ou no domínio de
// um cliente.
const API_BASE = getApiBase();

interface SignResponse {
  uploadUrl: string;
  publicUrl: string;
  key: string;
  kind: 'image' | 'video' | 'other';
}

// Tiny id (don't pull nanoid just for uploads; collision risk is irrelevant)
function uid(): string {
  return Math.random().toString(36).slice(2, 11) + Date.now().toString(36).slice(-4);
}

/**
 * Page-close survivor for /finalize. If the user closes the tab right after
 * the PUT to Wasabi succeeded but before the normal fetch round-trip
 * completes, sendBeacon flushes the request as the page unloads — the
 * server still records the upload and the bytes don't become orphaned.
 */
function finalizeWithBeacon(payload: object): boolean {
  if (typeof navigator === 'undefined' || !navigator.sendBeacon) return false;
  const token = localStorage.getItem('accessToken');
  // sendBeacon doesn't support custom headers; embed the token in the body
  // and have the server read it from there if Authorization is missing.
  const body = JSON.stringify({ ...payload, _bearer: token });
  return navigator.sendBeacon(
    `${API_BASE}/upload/finalize-beacon`,
    new Blob([body], { type: 'application/json' }),
  );
}

function authHeaders(): Record<string, string> {
  const token = typeof window !== 'undefined' ? localStorage.getItem('accessToken') : null;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function isHeic(file: File): boolean {
  const t = file.type.toLowerCase();
  if (t === 'image/heic' || t === 'image/heif') return true;
  // Older browsers / iOS share sheets sometimes leave the type empty
  const ext = file.name.split('.').pop()?.toLowerCase();
  return ext === 'heic' || ext === 'heif';
}

/**
 * Transcode HEIC/HEIF to JPEG q95 in the browser. q95 is visually
 * indistinguishable from the original — zero perceived quality loss.
 */
async function transcodeHeicToJpeg(file: File): Promise<File> {
  const heic2any = (await import('heic2any')).default;
  const blob = await heic2any({
    blob: file,
    toType: 'image/jpeg',
    quality: 0.95,
  });
  // heic2any can return Blob or Blob[]; normalise to a single blob
  const out = Array.isArray(blob) ? blob[0] : blob;
  const newName = file.name.replace(/\.(heic|heif)$/i, '.jpg');
  return new File([out], newName, { type: 'image/jpeg' });
}

// Above this, an image is re-encoded to WebP/JPEG before upload. A 360
// panorama saved as PNG is often 15-25 MB (lossless) — uploaded raw it takes
// 10-15s to download per scene and stalls the viewer. Re-encoding to WebP q0.9
// at FULL resolution drops it to ~2-3 MB with no visible quality loss.
const IMAGE_REENCODE_THRESHOLD = 2_500_000; // 2.5 MB

/**
 * Re-encode heavy images (any PNG, or any image over the threshold) to WebP
 * (falling back to JPEG) at full resolution. Photographic panoramas don't need
 * PNG's lossless alpha, so this is a pure win. Best-effort: any failure returns
 * the original file unchanged so an upload is never blocked.
 */
async function compressImage(file: File): Promise<File> {
  if (!file.type.startsWith('image/')) return file;
  const isPng = file.type === 'image/png' || file.name.toLowerCase().endsWith('.png');
  if (!isPng && file.size <= IMAGE_REENCODE_THRESHOLD) return file;

  try {
    const bitmap = await createImageBitmap(file);
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      bitmap.close?.();
      return file;
    }
    ctx.drawImage(bitmap, 0, 0);
    bitmap.close?.();

    const encode = (type: string, q: number) =>
      new Promise<Blob | null>((res) => canvas.toBlob((b) => res(b), type, q));

    // WebP first (best ratio); fall back to JPEG (e.g. width > 16383px, which
    // WebP can't encode, or a browser without WebP encode support).
    let blob = await encode('image/webp', 0.9);
    let ext = 'webp';
    let type = 'image/webp';
    if (!blob || blob.type !== 'image/webp') {
      blob = await encode('image/jpeg', 0.9);
      ext = 'jpg';
      type = 'image/jpeg';
    }

    if (!blob || blob.size >= file.size) return file; // no win — keep original
    const newName = file.name.replace(/\.[^.]+$/, '') + '.' + ext;
    return new File([blob], newName, { type });
  } catch {
    return file;
  }
}

/**
 * Normalise a file for browser delivery before upload:
 *   1. HEIC/HEIF → JPEG q95 (plays everywhere).
 *   2. Heavy/PNG images → WebP/JPEG q0.9 (fast to load, same look).
 */
async function normaliseForBrowser(file: File): Promise<File> {
  const deheic = isHeic(file) ? await transcodeHeicToJpeg(file) : file;
  return compressImage(deheic);
}

interface UploadOptions {
  /** Bucket sub-path under tenant prefix (e.g. 'thumbnails'). */
  subPath?: string;
  /** Optional progress callback (0..1). */
  onProgress?: (fraction: number) => void;
  /** For videos — pass to /finalize so the player skips metadata probe. */
  duration?: number;
  /** Display label for the upload indicator. Defaults to file.name. */
  label?: string;
  /** Hide this upload from the global indicator (used for tiny thumbnails). */
  silent?: boolean;
  /** Skip HEIC/compression normalisation (caller already normalised the file). */
  skipNormalise?: boolean;
  /** Local object-URL preview shown in the upload indicator (store revokes it). */
  thumbUrl?: string;
  /**
   * Vídeo acima do teto de textura: o servidor vai reduzi-lo e só então
   * cobrar a cota, pelo tamanho final. Sem isto, um 8K de 1,9 GB precisaria
   * caber em 1,9 GB de cota livre para poder subir — mesmo virando 236 MB.
   */
  pendingNormalization?: boolean;
}

/**
 * Upload a single file end-to-end. Returns the public URL.
 * The file is uploaded directly to Wasabi via a presigned URL — server
 * never sees the bytes. Progress is reported both to the caller's
 * callback and to the global useUploadStore so the floating
 * UploadIndicator widget reflects activity.
 */
export async function uploadAsset(
  file: File,
  opts: UploadOptions = {},
): Promise<{ publicUrl: string; key: string; bytesUsed: number; kind: SignResponse['kind'] }> {
  const normalised = opts.skipNormalise ? file : await normaliseForBrowser(file);
  const trackingId = uid();
  const store = useUploadStore.getState();

  if (!opts.silent) {
    store.start({
      id: trackingId,
      label: opts.label || normalised.name,
      kind: normalised.type.startsWith('video/')
        ? 'video'
        : normalised.type.startsWith('image/')
          ? 'image'
          : 'other',
      totalBytes: normalised.size,
      thumbUrl: opts.thumbUrl,
    });
  }

  const reportProgress = (f: number) => {
    if (!opts.silent) useUploadStore.getState().updateProgress(trackingId, f);
    opts.onProgress?.(f);
  };

  try {
    // 1. Sign
    const signRes = await fetch(`${API_BASE}/upload/sign`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({
        filename: normalised.name,
        contentType: normalised.type || 'application/octet-stream',
        fileSize: normalised.size,
        subPath: opts.subPath,
      }),
    });
    if (!signRes.ok) {
      const err = await signRes.json().catch(() => ({}));
      throw new Error(err.error || 'Failed to obtain upload URL');
    }
    const { data: sign } = (await signRes.json()) as { data: SignResponse };

    // 2. PUT to Wasabi directly. XHR for progress; fetch lacks upload progress.
    await new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', sign.uploadUrl, true);
      xhr.setRequestHeader('Content-Type', normalised.type || 'application/octet-stream');
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          // Reserve last 5% for the finalize step
          reportProgress((e.loaded / e.total) * 0.95);
        }
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) resolve();
        else reject(new Error(`Storage rejected upload (${xhr.status})`));
      };
      xhr.onerror = () => reject(new Error('Network error during upload'));
      xhr.send(normalised);
    });

    // 3. Finalize. Mark status so beforeunload knows we're past the safe point.
    if (!opts.silent) useUploadStore.getState().setStatus(trackingId, 'finalizing');

    const finalizePayload = {
      key: sign.key,
      fileSize: normalised.size,
      duration: opts.duration,
      pendingNormalization: opts.pendingNormalization,
    };

    // If the page is hiding RIGHT NOW, the fetch may not survive — flush via
    // sendBeacon. Server records the upload either way.
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      finalizeWithBeacon(finalizePayload);
    } else {
      const finRes = await fetch(`${API_BASE}/upload/finalize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(finalizePayload),
      });
      if (!finRes.ok) {
        const err = await finRes.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to finalize upload');
      }
    }

    if (!opts.silent) useUploadStore.getState().complete(trackingId);

    return {
      publicUrl: sign.publicUrl,
      key: sign.key,
      bytesUsed: normalised.size,
      kind: sign.kind,
    };
  } catch (err) {
    if (!opts.silent) {
      useUploadStore.getState().fail(trackingId, (err as Error).message);
    }
    throw err;
  }
}

/**
 * Generate a JPEG thumbnail Blob from an image File, in the browser.
 * Resized to fit within (width × height) preserving aspect ratio. Used by
 * uploadPanorama so we never re-encode the main asset server-side.
 *
 * 2048×1024, não 800×400.
 *
 * A miniatura não é mais exibida como está: ela é REPROJETADA para o ângulo
 * da cena (PanoThumb), e um recorte de 75° usa só ~21% da largura do
 * equirretangular. Com 800 de largura sobravam 167 pixels de origem para
 * encher 768 de saída — quatro vezes e meia de ampliação, e o resultado era
 * a imagem mole que se via nos cartões. Com 2048 sobram 427, e a conta cai
 * para menos de duas vezes.
 *
 * Custa ~250 KB por cena em vez de ~60 KB. Ao lado da panorâmica em si, que
 * tem alguns megabytes, é ruído — e é o que separa um cartão bonito de um
 * cartão embaçado.
 */
async function makeThumbnailBlob(
  file: File,
  width = 2048,
  height = 1024,
  quality = 0.85,
): Promise<Blob> {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error('Image decode failed'));
      i.src = url;
    });

    // Cover-fit (matches sharp's fit:'cover')
    const scale = Math.max(width / img.width, height / img.height);
    const drawW = img.width * scale;
    const drawH = img.height * scale;
    const dx = (width - drawW) / 2;
    const dy = (height - drawH) / 2;

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas 2D unavailable');
    ctx.drawImage(img, dx, dy, drawW, drawH);

    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error('toBlob returned null'))),
        'image/jpeg',
        quality,
      );
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Convenience: upload a panorama AND its thumbnail in one call.
 * Both go through the same /sign+/finalize flow. HEIC sources get
 * transparently transcoded to JPEG q95 before any of this happens.
 */
export async function uploadPanorama(
  file: File,
  opts: { onProgress?: (fraction: number) => void; thumbUrl?: string } = {},
): Promise<{ imageUrl: string; thumbnailUrl: string; bytesUsed: number }> {
  const normalised = await normaliseForBrowser(file);

  // Main: 90% of progress. Already normalised above — don't do it twice.
  const main = await uploadAsset(normalised, {
    skipNormalise: true,
    thumbUrl: opts.thumbUrl,
    onProgress: opts.onProgress ? (f) => opts.onProgress!(f * 0.9) : undefined,
  });

  // Thumbnail: 10% of progress
  const thumbBlob = await makeThumbnailBlob(normalised);
  const thumbFile = new File(
    [thumbBlob],
    normalised.name.replace(/\.[^.]+$/, '-thumb.jpg'),
    { type: 'image/jpeg' },
  );
  const thumb = await uploadAsset(thumbFile, {
    subPath: 'thumbnails',
    silent: true, // user only cares about the main asset
    onProgress: opts.onProgress ? (f) => opts.onProgress!(0.9 + f * 0.1) : undefined,
  });

  return {
    imageUrl: main.publicUrl,
    thumbnailUrl: thumb.publicUrl,
    bytesUsed: main.bytesUsed + thumb.bytesUsed,
  };
}

/**
 * Convenience: upload a video AND grab a poster frame as thumbnail.
 * Poster is captured at t=0.5s (first scrub-in frame) via <video> + canvas.
 */
export async function uploadVideo(
  file: File,
  opts: { onProgress?: (fraction: number) => void; thumbUrl?: string } = {},
): Promise<{ videoUrl: string; thumbnailUrl: string; duration: number; bytesUsed: number }> {
  // Grab poster + duration before upload (cheap — just decodes metadata)
  const { thumbBlob, duration, width } = await extractVideoPoster(file);

  // Video upload — 95% of progress
  const main = await uploadAsset(file, {
    duration,
    // Acima do teto o servidor reduz e cobra o resultado. Avisar aqui é o
    // que permite subir um 8K sem precisar de 1,9 GB de cota livre.
    pendingNormalization: width > SAFE_VIDEO_WIDTH,
    thumbUrl: opts.thumbUrl,
    onProgress: opts.onProgress ? (f) => opts.onProgress!(f * 0.95) : undefined,
  });

  // Thumbnail upload — 5% of progress
  const thumbFile = new File(
    [thumbBlob],
    file.name.replace(/\.[^.]+$/, '-thumb.jpg'),
    { type: 'image/jpeg' },
  );
  const thumb = await uploadAsset(thumbFile, {
    subPath: 'thumbnails',
    silent: true,
    onProgress: opts.onProgress ? (f) => opts.onProgress!(0.95 + f * 0.05) : undefined,
  });

  return {
    videoUrl: main.publicUrl,
    thumbnailUrl: thumb.publicUrl,
    duration,
    bytesUsed: main.bytesUsed + thumb.bytesUsed,
  };
}

async function extractVideoPoster(file: File): Promise<{ thumbBlob: Blob; duration: number; width: number }> {
  const url = URL.createObjectURL(file);
  try {
    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.preload = 'metadata';
    video.src = url;

    await new Promise<void>((resolve, reject) => {
      video.onloadedmetadata = () => resolve();
      video.onerror = () => reject(new Error('Video metadata decode failed'));
    });

    // Seek to 0.5s (or 10% of duration if shorter)
    const seekTime = Math.min(0.5, video.duration * 0.1);
    await new Promise<void>((resolve, reject) => {
      video.onseeked = () => resolve();
      video.onerror = () => reject(new Error('Video seek failed'));
      video.currentTime = seekTime;
    });

    const canvas = document.createElement('canvas');
    canvas.width = 800;
    canvas.height = 400;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas 2D unavailable');

    // Cover-fit
    const scale = Math.max(800 / video.videoWidth, 400 / video.videoHeight);
    const drawW = video.videoWidth * scale;
    const drawH = video.videoHeight * scale;
    ctx.drawImage(video, (800 - drawW) / 2, (400 - drawH) / 2, drawW, drawH);

    const thumbBlob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error('toBlob returned null'))),
        'image/jpeg',
        0.88,
      );
    });

    return { thumbBlob, duration: video.duration, width: video.videoWidth };
  } finally {
    URL.revokeObjectURL(url);
  }
}
